import { createServerFn } from "@tanstack/react-start";
import {
  builtInBlockForPath,
  isRecordTemplatePath,
  walkPageNodes,
} from "../../../shared/src/pages";
import { listPublicExperiences } from "../../../api/src/experiences/service";
import { readMatchupGuide, readMatchupLibrary } from "./matchups.server";
import { readBuiltIn } from "./built-in.server";
import { readDocumentPage } from "./document.server";
import { readNativeHome } from "./home.server";
import { privateResponse, siteContext } from "./site.server";

/**
 * What the editor's preview draws on the preview route: the
 * unsaved page, or the published homepage for a thumbnail or demo link. Anyone else finds nothing.
 */
export const loadPreviewPage = createServerFn({ method: "GET" }).handler(async ({ context }) => {
  const drate = await siteContext(context);
  if (
    !drate.creator ||
    !(drate.customizationPreview || drate.publishedWebsitePreview || drate.templatePreview)
  )
    return { kind: "missing" as const };
  privateResponse();
  if (drate.pageDocumentPreview) {
    const { document, record } = drate.pageDocumentPreview;
    // A guide template draws the guide picked in the editor in its guide block.
    if (isRecordTemplatePath(document.path)) {
      const page = await readDocumentPage(drate, document.path, { record });
      const experience = (await listPublicExperiences(drate)).find(
        (item) => item.kind === "matchups",
      );
      const [guide, library] =
        record && experience
          ? await Promise.all([
              readMatchupGuide(drate, record, undefined),
              readMatchupLibrary(drate, experience),
            ])
          : [null, null];
      return page.kind === "page"
        ? { ...page, builtIn: null, guide: guide && library ? { guide, library } : null }
        : page;
    }
    const page = await readDocumentPage(drate, document.path);
    // A composed feature page shows the feature's own content at its built-in block.
    const block = builtInBlockForPath(document.path);
    const builtIn =
      page.kind === "page" &&
      block &&
      page.document &&
      walkPageNodes(page.document).some((node) => node.kind === "builtIn")
        ? await readBuiltIn(drate, block)
        : null;
    return page.kind === "page" ? { ...page, builtIn } : page;
  }
  // A live-site thumbnail or demo link draws the published Home: composed, or the native one.
  if (drate.publishedWebsitePreview) {
    const home = await readDocumentPage(drate, "/");
    if (home.kind !== "missing" && home.kind !== "redirect") return home;
    return { kind: "home" as const, home: await readNativeHome(drate) };
  }
  return { kind: "native" as const };
});
